import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS, Styles } from 'react-joyride';
import { useLocation } from 'wouter';

type TourType = 'main' | 'inbox' | 'dashboard' | 'first-call' | 'first-sms' | 'contacts' | 'call-logs' | 'numbers' | 'campaigns' | 'voice-agents' | 'analytics' | 'team-settings' | 'ai-assistant' | null;

interface TourState {
  hasCompletedMainTour: boolean;
  hasCompletedInboxTour: boolean;
  hasCompletedDashboardTour: boolean;
  hasCompletedFirstCallTour: boolean;
  hasCompletedFirstSmsTour: boolean;
  hasCompletedContactsTour: boolean;
  hasCompletedCallLogsTour: boolean;
  hasCompletedNumbersTour: boolean;
  hasCompletedCampaignsTour: boolean;
  hasCompletedVoiceAgentsTour: boolean;
  hasCompletedAnalyticsTour: boolean;
  hasCompletedTeamSettingsTour: boolean;
  hasCompletedAiAssistantTour: boolean;
  hasSkippedTour: boolean;
}

interface ProductTourContextType {
  startTour: (tourType: TourType) => void;
  startTourIfNotCompleted: (tourType: TourType) => boolean;
  endTour: () => void;
  skipAllTours: () => void;
  resetTours: () => void;
  currentTour: TourType;
  tourState: TourState;
  isFirstVisit: boolean;
  showWelcomeModal: boolean;
  setShowWelcomeModal: (show: boolean) => void;
  isTourCompleted: (tourType: TourType) => boolean;
}

const ProductTourContext = createContext<ProductTourContextType | null>(null);

export function useProductTour() {
  const context = useContext(ProductTourContext);
  if (!context) {
    throw new Error('useProductTour must be used within ProductTourProvider');
  }
  return context;
}

const TOUR_STORAGE_KEY = 'limephone_tour_state';

const getStoredTourState = (): TourState => {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return {
    hasCompletedMainTour: false,
    hasCompletedInboxTour: false,
    hasCompletedDashboardTour: false,
    hasCompletedFirstCallTour: false,
    hasCompletedFirstSmsTour: false,
    hasCompletedContactsTour: false,
    hasCompletedCallLogsTour: false,
    hasCompletedNumbersTour: false,
    hasCompletedCampaignsTour: false,
    hasCompletedVoiceAgentsTour: false,
    hasCompletedAnalyticsTour: false,
    hasCompletedTeamSettingsTour: false,
    hasCompletedAiAssistantTour: false,
    hasSkippedTour: false,
  };
};

const customStyles: Partial<Styles> = {
  options: {
    arrowColor: '#111C33',
    backgroundColor: '#0F172A',
    overlayColor: 'rgba(11, 18, 32, 0.85)',
    primaryColor: 'hsl(var(--primary))',
    textColor: '#E6EBF5',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 12,
    padding: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipContent: {
    padding: '20px 24px',
  },
  tooltipTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
    color: '#E6EBF5',
  },
  tooltipFooter: {
    padding: '12px 24px',
    borderTop: '1px solid #111C33',
    marginTop: 0,
  },
  buttonNext: {
    backgroundColor: 'hsl(var(--primary))',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 500,
  },
  buttonBack: {
    color: '#A9B4CC',
    marginRight: 8,
  },
  buttonSkip: {
    color: '#7C8AA5',
    fontSize: 13,
  },
  buttonClose: {
    display: 'none',
  },
  spotlight: {
    borderRadius: 12,
  },
  beacon: {
    display: 'none',
  },
};

const mainTourSteps: Step[] = [
  {
    target: '[data-tour="sidebar"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">This is your command center</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Navigate between Inbox, Dashboard, and more. You'll spend most of your time in <strong className="text-text-layer-primary">Inbox</strong> — that's where the action happens.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '[data-tour="right-sidebar"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Your assistant panel</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          This panel follows you everywhere — quick dial, tasks, notes, and onboarding help. Click to expand anytime.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 4,
  },
];

const inboxTourSteps: Step[] = [
  {
    target: '[data-tour="inbox-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Never miss a customer</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          All calls, texts, and voicemails land here — even missed ones. Each conversation is organized by contact.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="inbox-filters"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Stay focused</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Filter by Open, Unread, or specific phone numbers. Focus on what matters most.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="conversation-view"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Reply, call, take action</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Send messages, make calls, or let AI draft responses for you. Everything you need is right here.
        </p>
        <div className="bg-background rounded-lg p-3 border border-border mt-3">
          <div className="flex items-center gap-2 text-green-400">
            <span>🎉</span>
            <span className="font-medium text-sm">You're all set!</span>
          </div>
          <p className="text-xs text-text-layer-muted mt-1">
            Customers won't slip through the cracks.
          </p>
        </div>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const dashboardTourSteps: Step[] = [
  {
    target: '[data-tour="dashboard-kpi"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Your daily metrics</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          See your performance at a glance — calls made, messages sent, and response times.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="dashboard-tabs"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Explore your dashboard</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed mb-3">
          Switch between views to see your calls, needs attention items, and follow-ups.
        </p>
        <div className="bg-background rounded-lg p-3 border border-border">
          <div className="flex items-center gap-2 text-green-400">
            <span>🎉</span>
            <span className="font-medium text-sm">You're all set!</span>
          </div>
          <p className="text-xs text-text-layer-muted mt-1">
            Check back daily to stay on top of your work.
          </p>
        </div>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const firstCallTourSteps: Step[] = [
  {
    target: '[data-tour="dialer-input"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Enter a number or search contacts</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Type a phone number directly or start typing a name to search your contacts.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="caller-id-selector"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Select which number to call from</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Choose which of your phone numbers will appear as the caller ID when you call.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="call-button"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Click to call</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          We'll connect you instantly. Your call will ring through your browser — no phone needed!
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const firstSmsTourSteps: Step[] = [
  {
    target: '[data-tour="sms-recipient"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Enter a number or pick a contact</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Type a phone number or search for an existing contact to message.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="sms-message-input"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Type your message here</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Compose your message. You can include emojis and links too!
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="sms-send-button"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Send!</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Your message will appear in the conversation instantly. Replies show up in your Inbox.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const contactsTourSteps: Step[] = [
  {
    target: '[data-tour="contact-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">All your contacts in one place</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          View, search, and manage all your contacts. Click any contact to see their full details.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="import-contacts"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Import contacts easily</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Import contacts from a CSV file or sync from Google Contacts. Get started quickly!
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="contact-actions"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Quick actions</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Click any contact to call or message them instantly. All your communication in one place.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const callLogsTourSteps: Step[] = [
  {
    target: '[data-tour="call-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Every call is logged here</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          All incoming, outgoing, and missed calls appear automatically. Never lose track of a conversation.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="call-filters"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Filter by type</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Show all calls, or filter by missed, incoming, or outgoing. Find what you need fast.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="call-details"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">See details and recordings</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Click any call to see details, play the recording, or view the AI transcript.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const numbersTourSteps: Step[] = [
  {
    target: '[data-tour="number-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Manage your phone numbers</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          All your phone numbers in one place. Each can have its own settings and configuration.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="number-settings"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Configure each number</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Set up call forwarding, voicemail greetings, and business hours for each number.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="add-number"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Get more numbers</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Add additional numbers for different teams, campaigns, or use cases.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const campaignsTourSteps: Step[] = [
  {
    target: '[data-tour="campaign-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">SMS and call campaigns</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Create bulk SMS campaigns or automated call sequences to reach multiple contacts at once.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="campaign-contacts"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Select your audience</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Upload a contact list or select from your existing contacts. Target exactly who you need.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="campaign-schedule"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Schedule or send now</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Send immediately or schedule for the perfect time. Track delivery and responses in real-time.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const voiceAgentsTourSteps: Step[] = [
  {
    target: '[data-tour="agent-list"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">AI agents that handle calls</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Create AI agents to answer calls, qualify leads, or provide support — 24/7.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="agent-settings"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Personality and knowledge</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Each agent has its own personality, voice, and knowledge base. Customize to match your brand.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="test-agent"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Test before going live</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Make a test call to hear how your agent sounds. Perfect it before customers call.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const analyticsTourSteps: Step[] = [
  {
    target: '[data-tour="analytics-charts"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Track your performance</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          See call volume, message stats, and response times at a glance.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="analytics-date-picker"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">View trends over time</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Switch between daily, weekly, and monthly views to spot patterns.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="analytics-sentiment"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">AI sentiment analysis</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          See how your conversations are going. AI analyzes tone to show customer satisfaction.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const teamSettingsTourSteps: Step[] = [
  {
    target: '[data-tour="invite-team"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Invite team members</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Add colleagues to collaborate on calls and messages. Work together seamlessly.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="team-roles"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Assign roles</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Choose Admin, Member, or View-only access. Control who can do what.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="number-assignment"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Assign numbers</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Each member can have their own number or share with the team.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

const aiAssistantTourSteps: Step[] = [
  {
    target: '[data-tour="ai-toggle"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Enable AI Assistant</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Toggle on AI-powered features to get smart suggestions for your conversations.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="ai-suggestions"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Smart reply suggestions</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          AI analyzes your conversations and suggests the best responses. Click to use or edit.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="ai-context"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Conversation insights</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          See customer sentiment, key topics, and recommended actions at a glance.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tour="ai-settings"]',
    content: (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-text-layer-primary">Customize AI behavior</h3>
        </div>
        <p className="text-text-layer-secondary text-sm leading-relaxed">
          Set your preferred tone, response length, and when AI should suggest replies.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

interface ProductTourProviderProps {
  children: ReactNode;
}

export function ProductTourProvider({ children }: ProductTourProviderProps) {
  const [location] = useLocation();
  const [currentTour, setCurrentTour] = useState<TourType>(null);
  const [tourState, setTourState] = useState<TourState>(getStoredTourState);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const state = getStoredTourState();
    setTourState(state);
    if (!state.hasCompletedMainTour && !state.hasSkippedTour) {
      setIsFirstVisit(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(tourState));
  }, [tourState]);

  const startTour = useCallback((tourType: TourType) => {
    if (!tourType) return;
    
    setCurrentTour(tourType);
    setStepIndex(0);
    
    switch (tourType) {
      case 'main':
        setSteps(mainTourSteps);
        break;
      case 'inbox':
        setSteps(inboxTourSteps);
        break;
      case 'dashboard':
        setSteps(dashboardTourSteps);
        break;
      case 'first-call':
        setSteps(firstCallTourSteps);
        break;
      case 'first-sms':
        setSteps(firstSmsTourSteps);
        break;
      case 'contacts':
        setSteps(contactsTourSteps);
        break;
      case 'call-logs':
        setSteps(callLogsTourSteps);
        break;
      case 'numbers':
        setSteps(numbersTourSteps);
        break;
      case 'campaigns':
        setSteps(campaignsTourSteps);
        break;
      case 'voice-agents':
        setSteps(voiceAgentsTourSteps);
        break;
      case 'analytics':
        setSteps(analyticsTourSteps);
        break;
      case 'team-settings':
        setSteps(teamSettingsTourSteps);
        break;
      case 'ai-assistant':
        setSteps(aiAssistantTourSteps);
        break;
    }
    
    setTimeout(() => {
      setRunTour(true);
    }, 300);
  }, []);

  const endTour = useCallback(() => {
    setRunTour(false);
    setCurrentTour(null);
    setStepIndex(0);
  }, []);

  const skipAllTours = useCallback(() => {
    setTourState(prev => ({
      ...prev,
      hasSkippedTour: true,
    }));
    endTour();
  }, [endTour]);

  const resetTours = useCallback(() => {
    const newState = {
      hasCompletedMainTour: false,
      hasCompletedInboxTour: false,
      hasCompletedDashboardTour: false,
      hasCompletedFirstCallTour: false,
      hasCompletedFirstSmsTour: false,
      hasCompletedContactsTour: false,
      hasCompletedCallLogsTour: false,
      hasCompletedNumbersTour: false,
      hasCompletedCampaignsTour: false,
      hasCompletedVoiceAgentsTour: false,
      hasCompletedAnalyticsTour: false,
      hasCompletedTeamSettingsTour: false,
      hasCompletedAiAssistantTour: false,
      hasSkippedTour: false,
    };
    setTourState(newState);
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(newState));
  }, []);

  const isTourCompleted = useCallback((tourType: TourType): boolean => {
    if (!tourType) return true;
    const tourCompletionMap: Record<string, keyof TourState> = {
      'main': 'hasCompletedMainTour',
      'inbox': 'hasCompletedInboxTour',
      'dashboard': 'hasCompletedDashboardTour',
      'first-call': 'hasCompletedFirstCallTour',
      'first-sms': 'hasCompletedFirstSmsTour',
      'contacts': 'hasCompletedContactsTour',
      'call-logs': 'hasCompletedCallLogsTour',
      'numbers': 'hasCompletedNumbersTour',
      'campaigns': 'hasCompletedCampaignsTour',
      'voice-agents': 'hasCompletedVoiceAgentsTour',
      'analytics': 'hasCompletedAnalyticsTour',
      'team-settings': 'hasCompletedTeamSettingsTour',
      'ai-assistant': 'hasCompletedAiAssistantTour',
    };
    const stateKey = tourCompletionMap[tourType];
    return stateKey ? Boolean(tourState[stateKey]) : true;
  }, [tourState]);

  const startTourIfNotCompleted = useCallback((tourType: TourType): boolean => {
    if (!tourType || tourState.hasSkippedTour) return false;
    if (isTourCompleted(tourType)) return false;
    startTour(tourType);
    return true;
  }, [tourState.hasSkippedTour, isTourCompleted, startTour]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if (finishedStatuses.includes(status)) {
      if (status === STATUS.FINISHED) {
        setTourState(prev => ({
          ...prev,
          ...(currentTour === 'main' && { hasCompletedMainTour: true }),
          ...(currentTour === 'inbox' && { hasCompletedInboxTour: true }),
          ...(currentTour === 'dashboard' && { hasCompletedDashboardTour: true }),
          ...(currentTour === 'first-call' && { hasCompletedFirstCallTour: true }),
          ...(currentTour === 'first-sms' && { hasCompletedFirstSmsTour: true }),
          ...(currentTour === 'contacts' && { hasCompletedContactsTour: true }),
          ...(currentTour === 'call-logs' && { hasCompletedCallLogsTour: true }),
          ...(currentTour === 'numbers' && { hasCompletedNumbersTour: true }),
          ...(currentTour === 'campaigns' && { hasCompletedCampaignsTour: true }),
          ...(currentTour === 'voice-agents' && { hasCompletedVoiceAgentsTour: true }),
          ...(currentTour === 'analytics' && { hasCompletedAnalyticsTour: true }),
          ...(currentTour === 'team-settings' && { hasCompletedTeamSettingsTour: true }),
          ...(currentTour === 'ai-assistant' && { hasCompletedAiAssistantTour: true }),
        }));
      }
      endTour();
    }

    if (action === ACTIONS.CLOSE) {
      endTour();
    }
  }, [currentTour, endTour]);

  return (
    <ProductTourContext.Provider
      value={{
        startTour,
        startTourIfNotCompleted,
        endTour,
        skipAllTours,
        resetTours,
        currentTour,
        tourState,
        isFirstVisit,
        showWelcomeModal,
        setShowWelcomeModal,
        isTourCompleted,
      }}
    >
      {children}
      <Joyride
        steps={steps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        hideCloseButton
        disableOverlayClose
        disableScrolling={false}
        scrollOffset={100}
        spotlightClicks
        callback={handleJoyrideCallback}
        styles={customStyles}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Done',
          next: 'Next',
          skip: 'Skip tour',
        }}
        floaterProps={{
          disableAnimation: false,
        }}
      />
    </ProductTourContext.Provider>
  );
}
